import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { MenuItem } from 'primeng/api';

import { MatchTypes } from '@core/interfaces';

@Component({
  selector: 'z-match-type',
  templateUrl: './match-type.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MatchTypeComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  selected: MatchTypes;

  open$ = new BehaviorSubject<boolean>(false);
  labelText$ = new BehaviorSubject<string>(null);
  titleText$ = new BehaviorSubject<string>(null);
  items$ = new BehaviorSubject<MenuItem[]>([]);

  @Input() type: 'string' | 'number' | 'date' = 'string';
  @Input() enableBetweenMatchType: boolean = false;
  @Input() inputGroup: boolean = false;
  @Input() controlWithoutLabel: boolean = false;
  @Input() defaultMatchType: MatchTypes = null;

  @Output() change = new EventEmitter<MatchTypes>();

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(private translocoService: TranslocoService) {}

  ngOnInit() {
    this.translocoService
      .selectTranslation()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.update();

        this.labelText$.next(this.translate('core.components.field_menu.label'));
        this.titleText$.next(null);
      });

    this.translocoService.langChanges$.pipe(takeUntil(this.destroy$)).subscribe((_lang) => {
      this.update();
      this.labelText$.next(this.translate('core.components.field_menu.label'));
      this.titleText$.next(null);
    });

    if (this.defaultMatchType) {
      this.setValue(this.defaultMatchType);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  setValue(value: MatchTypes) {
    if (!value && this.defaultMatchType) {
      value = this.defaultMatchType;
    }

    this.selected = value;
    this.change.emit(value);

    const matchType = this.getMatchType(value);
    let { label } = matchType;

    if (!value) {
      label = this.translate('core.components.field_menu.label');
    }

    this.labelText$.next(label);
    this.titleText$.next(matchType.title);

    this.update();
  }

  onShow(_event: any) {
    this.open$.next(true);
  }

  onHide(_event: any) {
    this.open$.next(false);
  }

  update() {
    const items: MenuItem[] = [];

    items.push({
      ...this.getMatchType(null),
      command: () => this.setValue(null),
      styleClass: !this.selected ? 'selected' : '',
    });

    if (this.type === 'string') {
      items.push({ separator: true });

      items.push({
        ...this.getMatchType('startsWith'),
        command: () => this.setValue('startsWith'),
        styleClass: this.selected === 'startsWith' ? 'selected' : '',
      });

      items.push({
        ...this.getMatchType('endsWith'),
        command: () => this.setValue('endsWith'),
        styleClass: this.selected === 'endsWith' ? 'selected' : '',
      });

      items.push({
        ...this.getMatchType('contains'),
        command: () => this.setValue('contains'),
        styleClass: this.selected === 'contains' ? 'selected' : '',
      });

      items.push({
        ...this.getMatchType('notContains'),
        command: () => this.setValue('notContains'),
        styleClass: this.selected === 'notContains' ? 'selected' : '',
      });
    }

    if (this.type === 'string' || this.type === 'number') {
      items.push({ separator: true });

      items.push({
        ...this.getMatchType('equals'),
        command: () => this.setValue('equals'),
        styleClass: this.selected === 'equals' ? 'selected' : '',
      });

      items.push({
        ...this.getMatchType('notEquals'),
        command: () => this.setValue('notEquals'),
        styleClass: this.selected === 'notEquals' ? 'selected' : '',
      });
    }

    if (this.type === 'number') {
      if (this.enableBetweenMatchType) {
        items.push({ separator: true });

        items.push({
          ...this.getMatchType('between'),
          command: () => this.setValue('between'),
          styleClass: this.selected === 'between' ? 'selected' : '',
        });
      }

      items.push({ separator: true });

      items.push({
        ...this.getMatchType('lt'),
        command: () => this.setValue('lt'),
        styleClass: this.selected === 'lt' ? 'selected' : '',
      });

      items.push({
        ...this.getMatchType('lte'),
        command: () => this.setValue('lte'),
        styleClass: this.selected === 'lte' ? 'selected' : '',
      });

      items.push({ separator: true });

      items.push({
        ...this.getMatchType('gt'),
        command: () => this.setValue('gt'),
        styleClass: this.selected === 'gt' ? 'selected' : '',
      });

      items.push({
        ...this.getMatchType('gte'),
        command: () => this.setValue('gte'),
        styleClass: this.selected === 'gte' ? 'selected' : '',
      });
    }

    if (this.type === 'date') {
      items.push({ separator: true });

      items.push({
        ...this.getMatchType('dateIs'),
        command: () => this.setValue('dateIs'),
        styleClass: this.selected === 'dateIs' ? 'selected' : '',
      });

      items.push({
        ...this.getMatchType('dateIsNot'),
        command: () => this.setValue('dateIsNot'),
        styleClass: this.selected === 'dateIsNot' ? 'selected' : '',
      });

      if (this.enableBetweenMatchType) {
        items.push({ separator: true });

        items.push({
          ...this.getMatchType('dateBetween'),
          command: () => this.setValue('dateBetween'),
          styleClass: this.selected === 'dateBetween' ? 'selected' : '',
        });
      }

      items.push({ separator: true });

      items.push({
        ...this.getMatchType('dateBefore'),
        command: () => this.setValue('dateBefore'),
        styleClass: this.selected === 'dateBefore' ? 'selected' : '',
      });

      items.push({
        ...this.getMatchType('dateLte'),
        command: () => this.setValue('dateLte'),
        styleClass: this.selected === 'dateLte' ? 'selected' : '',
      });

      items.push({ separator: true });

      items.push({
        ...this.getMatchType('dateAfter'),
        command: () => this.setValue('dateAfter'),
        styleClass: this.selected === 'dateAfter' ? 'selected' : '',
      });

      items.push({
        ...this.getMatchType('dateGte'),
        command: () => this.setValue('dateGte'),
        styleClass: this.selected === 'dateGte' ? 'selected' : '',
      });
    }

    this.items$.next(items);
  }

  getMatchType(value: string) {
    if (value === 'startsWith') {
      return {
        label: this.translate('core.components.field_menu.starts_with.label'),
        title: this.translate('core.components.field_menu.starts_with.title'),
      };
    }

    if (value === 'endsWith') {
      return {
        label: this.translate('core.components.field_menu.ends_with.label'),
        title: this.translate('core.components.field_menu.ends_with.title'),
      };
    }

    if (value === 'contains') {
      return {
        label: this.translate('core.components.field_menu.contains.label'),
        title: this.translate('core.components.field_menu.contains.title'),
      };
    }

    if (value === 'notContains') {
      return {
        label: this.translate('core.components.field_menu.does_not_contains.label'),
        title: this.translate('core.components.field_menu.does_not_contains.title'),
      };
    }

    if (value === 'equals') {
      return {
        label: this.translate('core.components.field_menu.equals.label'),
        title: this.translate('core.components.field_menu.equals.title'),
      };
    }

    if (value === 'notEquals') {
      return {
        label: this.translate('core.components.field_menu.does_not_equal.label'),
        title: this.translate('core.components.field_menu.does_not_equal.title'),
      };
    }

    if (value === 'between') {
      return {
        label: this.translate('core.components.field_menu.between.label'),
        title: this.translate('core.components.field_menu.between.title'),
      };
    }

    if (value === 'lt') {
      return {
        label: this.translate('core.components.field_menu.less_than.label'),
        title: this.translate('core.components.field_menu.less_than.title'),
      };
    }

    if (value === 'lte') {
      return {
        label: this.translate('core.components.field_menu.less_than_or_equals.label'),
        title: this.translate('core.components.field_menu.less_than_or_equals.title'),
      };
    }

    if (value === 'gt') {
      return {
        label: this.translate('core.components.field_menu.greater_than.label'),
        title: this.translate('core.components.field_menu.greater_than.title'),
      };
    }

    if (value === 'gte') {
      return {
        label: this.translate('core.components.field_menu.greater_than_or_equals.label'),
        title: this.translate('core.components.field_menu.greater_than_or_equals.title'),
      };
    }

    if (value === 'dateIs') {
      return {
        label: this.translate('core.components.field_menu.date_is.label'),
        title: this.translate('core.components.field_menu.date_is.title'),
      };
    }

    if (value === 'dateIsNot') {
      return {
        label: this.translate('core.components.field_menu.date_is_not.label'),
        title: this.translate('core.components.field_menu.date_is_not.title'),
      };
    }

    if (value === 'dateBetween') {
      return {
        label: this.translate('core.components.field_menu.date_between.label'),
        title: this.translate('core.components.field_menu.date_between.title'),
      };
    }

    if (value === 'dateBefore') {
      return {
        label: this.translate('core.components.field_menu.date_before.label'),
        title: this.translate('core.components.field_menu.date_before.title'),
      };
    }

    if (value === 'dateLte') {
      return {
        label: this.translate('core.components.field_menu.date_lte.label'),
        title: this.translate('core.components.field_menu.date_lte.title'),
      };
    }

    if (value === 'dateAfter') {
      return {
        label: this.translate('core.components.field_menu.date_after.label'),
        title: this.translate('core.components.field_menu.date_after.title'),
      };
    }

    if (value === 'dateGte') {
      return {
        label: this.translate('core.components.field_menu.date_gte.label'),
        title: this.translate('core.components.field_menu.date_gte.title'),
      };
    }

    return {
      label: this.translate('core.components.field_menu.default.label'),
      title: this.translate('core.components.field_menu.default.title'),
    };
  }
}
